import React, { useEffect, useState } from "react";
import useAlert from "../components/hook/useAlert";
import callApi from "../helpers/networkHelper";
import API_CONFIG from "../config/apiConstant";
import TrinetraDashboardSection from "../components/dashboard/TrinetraDashboardSection";
import Pagination from "../components/common/Pagination";
import PageHeader from "../components/common/PageHeader";
import DataTable from "../components/common/DataTable";
import EmptyState from "../components/common/EmptyState";
import { LayoutDashboard, User, Users, RadioTower, FileLock } from "lucide-react";

const LICENSE_PAGE_SIZE = 5;

const Dashboard = () => {
  const { showError } = useAlert();

  const [loading, setLoading] = useState(true);

  const [dashboardData, setDashboardData] = useState({
    clients: { total: 0, active: 0, inactive: 0 },
    partners: { total: 0, active: 0, inactive: 0 },
    rans: { total: 0, active: 0, inactive: 0 },
    licenses: { total: 0, active: 0, expired: 0 }
  });

  const [licenseOverview, setLicenseOverview] = useState([]);
  const [licensePage, setLicensePage] = useState(1);
  const [licenseTotalPages, setLicenseTotalPages] = useState(1);
  const [licenseTotal, setLicenseTotal] = useState(0);

  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchLicenseOverview();
    // eslint-disable-next-line
  }, [licensePage]);

  const fetchDashboard = async () => {
    try {
      const res = await callApi(
        API_CONFIG.DASHBOARD.SUMMARY.METHOD,
        API_CONFIG.DASHBOARD.SUMMARY.ENDPOINT
      );
      setDashboardData(res.data.data);
    } catch (err) {
      showError("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const fetchLicenseOverview = async () => {
    try {
      const res = await callApi(
        API_CONFIG.DASHBOARD.LICENSES.METHOD,
        `${API_CONFIG.DASHBOARD.LICENSES.ENDPOINT}?page=${licensePage}&limit=${LICENSE_PAGE_SIZE}`
      );

      const pagination = res.data.pagination || {};

      setLicenseOverview(res.data.data || []);
      setLicenseTotalPages(pagination.totalPages || 1);
      setLicenseTotal(
        pagination.total ?? pagination.totalRecords ?? (res.data.data || []).length
      );
    } catch (err) {
      showError("Failed to load license overview");
    }
  };

  const getLicenseStatus = (license) => {
    if (license.expiresAt && new Date() > new Date(license.expiresAt)) {
      return "EXPIRED";
    }
    return "ACTIVE";
  };

  const overviewColumns = [
    { key: "client", label: "Client", render: (l) => l.client?.clientName || "—" },
    { key: "partner", label: "Partner", render: (l) => l.partner?.partnerName || "—" },
    { key: "ran", label: "RAN", nowrap: true, render: (l) => l.ran?.ranID || "—" },
    {
      key: "licenseId",
      label: "License ID",
      nowrap: true,
      render: (l) => (
        <span className="font-medium" style={{ color: "var(--brand)" }}>
          {l.licenseId}
        </span>
      ),
    },
    { key: "licenseType", label: "Type", nowrap: true },
    {
      key: "status",
      label: "Status",
      nowrap: true,
      render: (l) => {
        const status = getLicenseStatus(l);
        return (
          <span
            className="px-3 py-1 rounded-full text-xs font-semibold border"
            style={{
              color: status === "ACTIVE" ? "var(--success)" : "var(--danger)",
              borderColor: status === "ACTIVE" ? "var(--success-border)" : "var(--danger-border)",
              background: status === "ACTIVE" ? "var(--success-bg)" : "var(--danger-bg)",
            }}
          >
            {status}
          </span>
        );
      },
    },
    {
      key: "expiresAt",
      label: "Expiry",
      nowrap: true,
      render: (l) =>
        l.expiresAt ? new Date(l.expiresAt).toLocaleDateString("en-GB") : "Never",
    },
  ];

  return (
    <div
      className="p-4 sm:p-6"
      style={{
        background: "var(--main-bg)",
        color: "var(--main-text)",
        minHeight: "100vh"
      }}
    >
      <PageHeader
        icon={<LayoutDashboard size={28} strokeWidth={2.2} />}
        title="Dashboard"
        subtitle="License Management Summary"
      />

      {/* SUMMARY CARDS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-10">
        <StatCard
          title="Clients"
          data={dashboardData.clients}
          loading={loading}
          icon={<User size={28} strokeWidth={2} style={{ color: "var(--brand)" }} />}
        />
        <StatCard
          title="Partners"
          data={dashboardData.partners}
          loading={loading}
          icon={<Users size={28} strokeWidth={2} style={{ color: "var(--brand)" }} />}
        />
        <StatCard
          title="RANs"
          data={dashboardData.rans}
          loading={loading}
          icon={<RadioTower size={28} strokeWidth={2} style={{ color: "var(--brand)" }} />}
        />
        <StatCard
          title="Licenses"
          data={dashboardData.licenses}
          loading={loading}
          icon={<FileLock size={28} strokeWidth={2} style={{ color: "var(--brand)" }} />}
        />
      </div>

      {/* LICENSE OVERVIEW */}
      <section aria-labelledby="license-overview-heading">
        <div className="mb-3">
          <h2
            id="license-overview-heading"
            className="text-xl font-semibold"
            style={{ color: "var(--main-text)" }}
          >
            License Overview
          </h2>
          <p className="text-sm mt-0.5" style={{ color: "var(--main-text-muted)" }}>
            Client → Partner → RAN → License mapping
          </p>
        </div>

        <DataTable
          columns={overviewColumns}
          rows={licenseOverview}
          loading={loading}
          skeletonRows={3}
          getRowKey={(l) => l._id}
          emptyState={
            <EmptyState
              compact
              icon={<FileLock size={26} />}
              title="No licenses issued yet"
              description="RAN and Compass licenses appear here once generated."
            />
          }
        />

        <Pagination
          page={licensePage}
          totalPages={licenseTotalPages}
          totalRecords={licenseTotal}
          onPageChange={setLicensePage}
        />
      </section>

      {/* TRINETRA SECTION */}
      <TrinetraDashboardSection onError={showError} />
    </div>
  );
};

export default Dashboard;

// StatCard component
//
// Takes `loading` rather than being hidden behind a page-wide spinner: the
// cards keep their size while the numbers resolve, so the layout does not jump
// once the request lands.
const StatCard = ({ title, data, icon, loading = false }) => (
  <div
    className="rounded-lg p-5 flex flex-col gap-2 shadow"
    style={{
      background: "var(--card-bg)",
      border: "1px solid var(--card-border)",
      minHeight: 140
    }}
  >
    <div className="flex items-center gap-2 mb-2">
      {icon}
      <h3 className="text-lg font-semibold" style={{ color: "var(--main-text)" }}>{title}</h3>
    </div>
    <div className="space-y-1 text-md font-medium">
      <StatRow label="Total" value={data.total} color="var(--brand)" loading={loading} />
      <div style={{ borderBottom: "1px solid var(--card-border)" }} />
      <StatRow label="Active" value={data.active} color="var(--success)" loading={loading} />
      {"inactive" in data && (
        <StatRow label="Inactive" value={data.inactive} color="var(--danger)" loading={loading} />
      )}
      {"expired" in data && (
        <StatRow label="Expired" value={data.expired} color="var(--danger)" loading={loading} />
      )}
    </div>
  </div>
);

const StatRow = ({ label, value, color, loading }) => (
  <div className="flex justify-between items-center">
    <span style={{ color: "var(--main-text-muted)" }}>{label}</span>
    {loading ? (
      <span
        className="h-4 w-8 rounded animate-pulse"
        style={{ background: "var(--card-border)" }}
      />
    ) : (
      <span className="font-semibold" style={{ color }}>{value}</span>
    )}
  </div>
);